import React, { useEffect, useState } from 'react';
import { View, Text, FlatList, TouchableOpacity, Modal, StyleSheet, useWindowDimensions } from 'react-native';
import { fetchCategories, createCategory, updateCategory, deleteCategory } from '../transactionsRepository';
import CategoryForm from './CategoryForm';
import theme from '../../../theme';
import { useI18n } from '../../../i18n/useI18n';
import AppButton from '../../../components/AppButton';

type Category = {
  id: string;
  name: string;
  type: 'income' | 'expense';
  color?: string;
  user_id?: string | null;
};

type Props = {
  userId: string;
  onClose: () => void;
  lang?: string;
  presentation?: 'modal' | 'inline';
};

export default function CategoriesManager({ userId, onClose, lang, presentation = 'modal' }: Props) {
  const { t } = useI18n(lang);
  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState<Category | null>(null);
  const [showForm, setShowForm] = useState(false);
  const { width } = useWindowDimensions();
  const isWide = width >= 700;

  async function load() {
    setLoading(true);
    const data = await fetchCategories(userId).catch(() => []);
    setCategories(data as Category[]);
    setLoading(false);
  }

  useEffect(() => { load(); }, [userId]);

  async function handleCreate(payload: { name: string; color?: string; type: 'income' | 'expense' }) {
    const created = await createCategory(userId, payload);
    if (created) {
      setShowForm(false);
      load();
    }
  }

  async function handleSaveEdit(payload: { name: string; color?: string; type: 'income' | 'expense' }) {
    if (!editing) return;
    await updateCategory(editing.id, payload);
    setEditing(null);
    setShowForm(false);
    load();
  }

  async function handleDelete(id: string) {
    await deleteCategory(id);
    load();
  }

  // keep containerStyle always as an array to avoid nested-array style typing issues
  const containerStyle: Array<any> = presentation === 'modal'
    ? [
      styles.containerModal,
      isWide
        ? ({ alignSelf: 'center', width: '100%', maxWidth: 420 } as any)
        : ({ width: '100%', maxWidth: undefined } as any),
    ]
    : [styles.containerInline, isWide ? ({ alignSelf: 'center', width: '100%', maxWidth: 420 } as any) : null];

  const content = (
    <View
      style={[
        ...containerStyle,
        { backgroundColor: theme.colors.background },
      ]}
    > 
      <Text style={[styles.title, { color: theme.colors.textPrimary }]}>{t('manageCategories')}</Text>
      <AppButton title={t('close')} onPress={onClose} tone="secondary" variant="outline" />

      <View style={{ height: 8 }} />
      <AppButton title={t('addCategory')} onPress={() => { setEditing(null); setShowForm(true); }} tone="primary" />

      {presentation === 'inline' && showForm ? (
        <View style={{ marginTop: 12 }}>
          <CategoryForm
            initial={editing ? { name: editing.name, color: editing.color, type: editing.type } : undefined}
            onCancel={() => setShowForm(false)}
            onSave={(p) => editing ? handleSaveEdit(p) : handleCreate(p)}
            lang={lang}
          />
        </View>
      ) : (
        <FlatList
          data={categories}
          keyExtractor={(item) => item.id}
          renderItem={({ item }) => (
            <View style={[styles.item, { borderColor: theme.colors.border }]}> 
              <View>
                <View style={{ flexDirection: 'row', alignItems: 'center' }}>
                  <Text
                    style={{
                      width: 18,
                      textAlign: 'center',
                      marginRight: 6,
                      fontWeight: '800',
                      color: item.type === 'income' ? theme.colors.success : theme.colors.error,
                    }}
                  >
                    {item.type === 'income' ? '↑' : '↓'}
                  </Text>
                  {!!item.color && (
                    <View
                      style={{
                        width: 10,
                        height: 10,
                        borderRadius: 5,
                        marginRight: 8,
                        backgroundColor: item.color,
                        borderWidth: 1,
                        borderColor: theme.colors.border,
                      }}
                    />
                  )}
                  <Text style={{ fontWeight: item.user_id ? '700' : '400', color: theme.colors.textPrimary }}>
                    {item.name}
                  </Text>
                </View>
                <Text style={{ color: theme.colors.textSecondary }}>{item.type}</Text>
              </View>
              <View style={styles.actions}>
                <>
                  <TouchableOpacity onPress={() => { setEditing(item); setShowForm(true); }} style={styles.link}><Text style={{ color: theme.colors.primary }}>{t('edit')}</Text></TouchableOpacity>
                  <TouchableOpacity onPress={() => handleDelete(item.id)} style={styles.link}><Text style={{ color: theme.colors.error }}>{t('delete')}</Text></TouchableOpacity>
                </>
              </View>
            </View>
          )}
        />
      )}

      {presentation === 'modal' && (
        <Modal visible={showForm} animationType="slide">
          <View style={{ flex: 1, padding: 0, alignItems: 'stretch', justifyContent: 'flex-start' }}>
            <CategoryForm
              initial={editing ? { name: editing.name, color: editing.color, type: editing.type } : undefined}
              onCancel={() => setShowForm(false)}
              onSave={(p) => editing ? handleSaveEdit(p) : handleCreate(p)}
              lang={lang}
              // allow form to expand full-width on narrow screens
              fullScreen
            />
          </View>
        </Modal>
      )}
    </View>
  );

  if (presentation === 'modal') {
    return (
      <Modal visible={true} animationType="slide">
        {content}
      </Modal>
    );
  }

  return content;
}

const styles = StyleSheet.create({
  containerModal: { flex: 1, padding: 16 },
  containerInline: { flex: 1, padding: 16 },
  title: { fontSize: 18, fontWeight: '700', marginBottom: 12 },
  item: { flexDirection: 'row', justifyContent: 'space-between', paddingVertical: 12, borderBottomWidth: 1 },
  actions: { flexDirection: 'row', alignItems: 'center' },
  link: { marginLeft: 12 }
});
